import * as Sentry from '@sentry/browser';

import {
  ProcessPaymentResponse,
  ResultActionDto,
} from '@cashier/cashier-api-model';
import {
  AttributeErrorBag,
  InputTypes,
  PaymentActionError,
  PaymentActionResult,
  PaymentMethodInput,
  PaymentStates,
  ResultAction,
  ResultActionContainers,
  ResultActionMethods,
  ResultActionTypes,
} from '@cashier/cashier-model';

interface PaymentActionMapper {
  toResultAction: (dto: ResultActionDto) => ResultAction;
  toPaymentActionResult: (
    response: ProcessPaymentResponse,
  ) => PaymentActionResult;
  toPaymentActionError: (
    response: ProcessPaymentResponse,
  ) => PaymentActionError;
}

const toResultAction = (dto: ResultActionDto): ResultAction => {
  try {
    return {
      type: dto.type as ResultActionTypes,
      url: dto.url,
      method: dto.method as ResultActionMethods,
      parameters: dto.parameters,
      container: dto.container as ResultActionContainers,
      height: dto.height,
      width: dto.width,
      output: dto.output || dto.html,
      fields: dto.fields
        ? dto.fields.map(
            (field) =>
              ({
                ...field,
                type: field.type as InputTypes,
              }) as PaymentMethodInput,
          )
        : [],
    };
  } catch (error) {
    Sentry.captureException(
      new Error('####Mapping to ResultAction Failure. Data####: '),
      {
        extra: {
          input: dto,
          error,
        },
      },
    );
  }
};

const toAttributeErrorBag = (data: {
  [key: string]: string;
}): AttributeErrorBag | null => {
  const isEmptyObject = (obj: object) => Object.keys(obj).length === 0;

  if (isEmptyObject(data)) return null;

  const errorBag: AttributeErrorBag = {};

  Object.keys(data).forEach((key) => {
    errorBag[key] = [data[key]];
  });

  return errorBag;
};

export const PaymentActionMapper: PaymentActionMapper = {
  toResultAction: (dto: ResultActionDto): ResultAction => toResultAction(dto),
  toPaymentActionResult: (
    response: ProcessPaymentResponse,
  ): PaymentActionResult => {
    const resultAction = response.resultAction || response.redirectOutput;

    try {
      return {
        transactionId: response.transactionId,
        status: (response.status || response.transactionState) as PaymentStates,
        resultAction: resultAction ? toResultAction(resultAction) : null,
        description: response.description || null,
        paymentAttributes: response.paymentAttributes
          ? response.paymentAttributes.map(
              (field) =>
                ({
                  ...field,
                  type: field.type as InputTypes,
                }) as PaymentMethodInput,
            )
          : [],
      };
    } catch (error) {
      Sentry.captureException(
        new Error('####Mapping to PaymentAction Result Failure. Data####: '),
        {
          extra: {
            input: response,
            error,
          },
        },
      );
    }
  },
  toPaymentActionError: (
    response: ProcessPaymentResponse,
  ): PaymentActionError => {
    try {
      return {
        message: response.message || response.error || 'Something went wrong.',
        attributeErrorBag: response.data
          ? toAttributeErrorBag(response.data)
          : response.errors || null,
        errorDetails: response.errorDetails || null,
        transactionId: response.transactionId || null,
      };
    } catch (error) {
      Sentry.captureException(
        new Error('####Mapping to PaymentAction Error Failure. Data####: '),
        {
          extra: {
            input: response,
            error,
          },
        },
      );
    }
  },
};
